/**
 * 外部SA オンボーディング/契約のロジック (純関数)。
 * functions と admin-web の双方で同一ルールを使うため shared に集約。
 *
 * 仕様:
 *  - ステータスは 3 フラグから導出する (computeSaStatus)。
 *  - 契約開始日 = 「登録完了 (同意 + 入金) になった月の翌月 1 日」(JST)。
 *  - 契約終了日 = 開始日 + 1年 − 1日。
 *  - 年次更新 = 前期終了日の翌日が新開始日。
 */
import type { SaOnboardingStatus } from '../types/common';

/** 同意+入金で 'registered'、さらに受講で 'active'、未満は 'provisional'。 */
export function computeSaStatus(flags: {
  consentConfirmed?: boolean;
  paymentConfirmed?: boolean;
  briefingAttended?: boolean;
}): SaOnboardingStatus {
  if (!flags.consentConfirmed || !flags.paymentConfirmed) return 'provisional';
  if (!flags.briefingAttended) return 'registered';
  return 'active';
}

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * 指定時刻が属する月 (JST) の「翌月 1 日」を 'YYYY-MM-DD' で返す。
 * 例: 2026-06-12 (JST) → '2026-07-01' / 2026-12-30 → '2027-01-01'。
 * 日付は無視し月だけを見るため、月初・月末いずれの登録完了でも翌月 1 日に揃う。
 */
export function nextMonthFirstJst(now: Date): string {
  const jst = new Date(now.getTime() + JST_OFFSET_MS);
  let year = jst.getUTCFullYear();
  let month = jst.getUTCMonth() + 1; // 1-12 (当月)
  month += 1; // 翌月
  if (month > 12) {
    month = 1;
    year += 1;
  }
  return `${year}-${pad2(month)}-01`;
}

/**
 * 契約開始日 'YYYY-MM-DD' から終了日 (開始日 + N年 − 1日) を返す。
 * years 既定 1。例: '2026-07-01', 1 → '2027-06-30' / 2 → '2028-06-30'。
 */
export function contractEndFromStart(start: string, years = 1): string {
  const [y, m, d] = start.split('-').map(Number);
  const span = Number.isFinite(years) && years >= 1 ? Math.floor(years) : 1;
  // 開始 + N年 の前日 (UTC 上の純カレンダー計算。タイムゾーン非依存)
  const dt = new Date(Date.UTC(y + span, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/**
 * 年次更新時の次期開始日 = 前期終了日の翌日 ('YYYY-MM-DD')。
 * 例: 前期終了 '2027-06-30' → 次期開始 '2027-07-01'。
 */
export function nextTermStartFromEnd(prevEnd: string): string {
  const [y, m, d] = prevEnd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/**
 * 指定日 (YYYY-MM-DD) までの残り日数を JST 基準で返す (当日=0、過去は負)。
 * 更新リマインダーの「終了日の N日前」判定に使う。
 */
export function daysUntilDateJst(targetYmd: string, now: Date): number {
  const [y, m, d] = targetYmd.split('-').map(Number);
  const target = Date.UTC(y, m - 1, d);
  const jst = new Date(now.getTime() + JST_OFFSET_MS);
  const today = Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate());
  return Math.round((target - today) / (24 * 60 * 60 * 1000));
}
